import Cookies from 'js-cookie'
import * as amplitude from '@amplitude/unified'
import configment from '@adc/configment'
import instrument from '@adc/instrument'
import * as graph from '@adc/sdk'
import type { Listing } from '@adc/sdk/types'
import { utils as blueprintUtils } from '@adc/blueprint'
import type { primitives, search } from '@adc/blueprint'
import eventNames from './eventNames'
import standardTracking from './standardTracking'
import { getAnalyticsHashedEmail, setAnalyticsHashedEmail } from './session'
import { hasAdvertisingConsent } from './consent'
import { hashEmail } from './hashEmail'
import { SEARCH_FILTER_LABELS } from './searchFilterLabels'
import * as helpers from './helpers'
import { readGoogleClickId } from './google_click_id'
import {
  getAmplitudeEventConfig,
  getPageClassification,
} from '../amplitude/helpers'
import { identifyUser } from '../amplitude/index'

// Type definitions
interface TrackingProps {
  [key: string]: any
}

interface TrackUserDomainEventParams {
  domainEventTypeName: graph.enums.DomainEventTypeName
  user: primitives.AuctionUser
  data?: Record<string, any>
  source?: string
  urlString?: string
}

declare global {
  interface Window {
    dataLayer?: any[]
  }
}

const HASHED_EMAIL_EVENTS: ReadonlySet<string> = new Set([
  eventNames.accountCreate,
  eventNames.accountLogin,
  eventNames.propertySave,
  eventNames.documentDownload,
  eventNames.confirmBid,
  eventNames.confirmProxyBid,
  eventNames.offerSubmissionSubmit,
  eventNames.registrationFlowCompleted,
])

function pushToDataLayer(event: string, props: TrackingProps): void {
  window.dataLayer!.push({
    event,
    ...props,
  })

  // Amplitude
  const eventConfig = getAmplitudeEventConfig(event)
  if (eventConfig?.enabled && eventConfig.name) {
    const eventName = eventConfig.name
    const data = eventConfig.extractData
      ? eventConfig.extractData(props)
      : props

    amplitude.track(eventName, data)

    // Auto-identify user on auth events
    if (event === 'account-login' || event === 'account-create') {
      const userId = props.userId || props.user_id
      if (userId) {
        identifyUser(String(userId))
      }
    }
  }
}

function track(event: string, props: TrackingProps = {}): void {
  if (typeof window === 'undefined' || !window.dataLayer) return

  if (HASHED_EMAIL_EVENTS.has(event) && hasAdvertisingConsent()) {
    const hashedEmail = getAnalyticsHashedEmail()
    if (hashedEmail) {
      pushToDataLayer(event, { ...props, hashedEmail })
      return
    }
  }

  pushToDataLayer(event, props)
}

function trackDataLayerLoaded(
  user: primitives.AuctionUser = { logged_in: false },
  additionalData: TrackingProps = {}
): void {
  // Set hashedEmail so subsequent approved events can attach it.
  if (user?.logged_in && (user as any).email && hasAdvertisingConsent()) {
    hashEmail((user as any).email)
      .then(setAnalyticsHashedEmail)
      .catch(() => setAnalyticsHashedEmail(null))
  } else {
    setAnalyticsHashedEmail(null)
  }

  const { google_click_id } = readGoogleClickId()
  track(eventNames.dataLayerLoaded, {
    cookieId: Cookies.get('BRID'),
    userLoggedIn: user.logged_in,
    ...(user?.logged_in ? standardTracking.user(user) : {}),
    ...(google_click_id ? { google_click_id } : {}),
    ...additionalData,
  })
}

// Buying-preferences nudge analytics. Fired by the @adc/ui-adc
// useProgressiveCapture hook for nudge_shown/completed/skipped/dismissed. Adds
// the standard user identifiers plus a pageClassification so the surface the
// user was nudged on (Homepage / SRP / PDP / Calendar / Dashboard) is
// segmentable in Amplitude — defaultTracking is off, so page is not autocaptured.
function trackBuyingPreferencesNudge(
  user: primitives.AuctionUser = { logged_in: false },
  event: string,
  properties: TrackingProps = {}
): void {
  track(event, {
    userLoggedIn: user.logged_in,
    pageClassification: getPageClassification(),
    ...standardTracking.user(user),
    ...properties,
  })
}

function trackPropertySave(
  listing: Listing,
  action: string,
  additionalData: TrackingProps = {}
): void {
  track(eventNames.propertySave, {
    action,
    status: 'success',
    ...additionalData,
    ...standardTracking.listing(listing),
  })
}

const defaultSource = (): string => `adc:service:${configment.get('APP_NAME')}`

const trackInstrumentPageLoad = (
  user: primitives.AuctionUser = { logged_in: false },
  productLine: string,
  source: string = defaultSource()
): void => {
  trackUserDomainEvent({
    domainEventTypeName: graph.enums.DomainEventTypeName.PageViewed,
    user,
    source,
    data: {
      product_line: productLine,
    },
  })
}

// remove this, call it from apps and just pass in listing.urn
const trackUserDomainEvent = ({
  domainEventTypeName,
  user = { logged_in: false },
  data = {},
  source = defaultSource(),
  urlString = window.location.href,
}: TrackUserDomainEventParams): void => {
  if (!('marketing' in data)) {
    const marketingData =
      blueprintUtils.extractMarketingSupportPrefixes(urlString) || {}
    if (Object.keys(marketingData).length > 0) {
      data.marketing = marketingData
    }
  }

  instrument.domainEvent({
    // @ts-ignore
    type: domainEventTypeName,
    subject: helpers.getUrn(user),
    source,
    data,
  })
}

// Amplitude event for a search filter change. Only fires for filters with a
// label in SEARCH_FILTER_LABELS; logs label + previous/current value.
const trackSearchFilterChanged = (change: search.types.FilterChange): void => {
  const filterLabel = SEARCH_FILTER_LABELS[change.name]
  if (!filterLabel) return
  track(eventNames.searchFilterChanged, {
    filter_label: filterLabel,
    previous_value: change.previousValue,
    current_value: change.currentValue,
  })
}

export {
  defaultSource,
  eventNames,
  track,
  trackDataLayerLoaded,
  trackBuyingPreferencesNudge,
  trackPropertySave,
  trackSearchFilterChanged,
  trackInstrumentPageLoad,
  trackUserDomainEvent,
}

// Type exports
export type { TrackingProps, TrackUserDomainEventParams }
